import i18n from '@/utils/i18n';
import {
  formatBlockCount,
  formatBlockCurrency,
  formatHubPercentage,
  resolveWeeklyImpact,
} from '@sales-portal/domain/pricing/screens/overview/blocs/utils/pricing-hub-formatters';
import {
  GetPricingHubAgentRecsQuery,
  GetPricingHubCostChangesQuery,
  GetPricingHubCustomersQuery,
} from '@/types';
import { PricingHubBlockMetrics } from '@sales-portal/domain/pricing/screens/overview/types';
import { PricingHubBookmarkCounts } from '@sales-portal/domain/pricing/screens/overview/blocs/utils/pricing-hub-bookmark-counts';
import { useMemo } from 'react';

interface Params {
  agentRecs: GetPricingHubAgentRecsQuery['getPricingHubAgentRecs'] | undefined;
  bookmarks: PricingHubBookmarkCounts | undefined;
  costChanges:
    | GetPricingHubCostChangesQuery['getPricingHubCostChanges']
    | undefined;
  currencyCode: string;
  customers: GetPricingHubCustomersQuery['getPricingHubCustomers'] | undefined;
}

export const usePricingHubBlocksTransformer = ({
  agentRecs,
  bookmarks,
  costChanges,
  currencyCode,
  customers,
}: Params): PricingHubBlockMetrics =>
  useMemo(() => {
    const agentRecsImpact = resolveWeeklyImpact(
      agentRecs?.weekly_gp_impact_in_micros,
      currencyCode,
    );
    const costChangesImpact = resolveWeeklyImpact(
      costChanges?.weekly_gp_impact_in_micros,
      currencyCode,
    );

    return {
      agentRecs: {
        caption: i18n.t('pricing.overview.agent_recs.caption'),
        footer: agentRecsImpact.text,
        footerDirection: agentRecsImpact.direction,
        value: formatBlockCount(agentRecs?.to_review_count),
      },
      bookmarked: {
        caption: i18n.t('pricing.overview.bookmarked.caption'),
        footer: i18n.t('pricing.overview.bookmarked.footer', {
          accounts: bookmarks?.restaurantCount ?? 0,
          items: bookmarks?.variantPackCount ?? 0,
        }),
        value: formatBlockCount(bookmarks?.bookmarkCount),
      },
      costChanges: {
        caption: i18n.t('pricing.overview.cost_changes.caption'),
        footer: costChangesImpact.text,
        footerDirection: costChangesImpact.direction,
        value: formatBlockCount(costChanges?.item_count),
      },
      customers: {
        caption: i18n.t('pricing.overview.customers.caption'),
        footer: i18n.t('pricing.overview.customers.footer', {
          grossProfit: formatBlockCurrency(
            customers?.weekly_gross_profit_in_micros,
            currencyCode,
          ),
          revenue: formatBlockCurrency(
            customers?.weekly_revenue_in_micros,
            currencyCode,
          ),
        }),
        value: formatHubPercentage(customers?.realized_gm_percentage),
      },
    };
  }, [agentRecs, bookmarks, costChanges, currencyCode, customers]);
